import { gql, useQuery } from 'urql';
import { useMemo } from 'react';
import {
  FormData,
  FormComponent,
  COMPONENT_TYPE_GUEST,
  COMPONENT_TYPE_IMAGE,
  COMPONENT_TYPE_LOGO,
  COMPONENT_TYPE_TITLE,
  COMPONENT_TYPE_TEXT,
  COMPONENT_TYPE_CHECKBOX,
  COMPONENT_TYPE_RADIOBUTTON,
  COMPONENT_TYPE_BUTTON,
} from '@entities/leadCollector';

export const GET_FORM_BY_LINK_ID_QUERY = gql`
  query GetFormByLinkId($formLink: String!) {
    getFormByLinkId(formLink: $formLink) {
      maxExtraChildren
      maxExtraAdults
      bodyForm
      formLinkStatus
      eventName
      dateStart
      dateEnd
    }
  }
`;

export interface GetFormByLinkIdResponse {
  getFormByLinkId: {
    maxExtraChildren: number;
    maxExtraAdults: number;
    bodyForm: FormData | null;
    formLinkStatus: string;
    eventName: string;
    dateStart: string;
    dateEnd: string;
  };
}

const normalizeFormData = (bodyForm: FormData | null): FormData | undefined => {
  if (!bodyForm?.components) {
    return undefined;
  }

  const typeMap: Record<string, string> = {
    forms: COMPONENT_TYPE_GUEST,
    image: COMPONENT_TYPE_IMAGE,
    logo: COMPONENT_TYPE_LOGO,
    title: COMPONENT_TYPE_TITLE,
    text: COMPONENT_TYPE_TEXT,
    checkbox: COMPONENT_TYPE_CHECKBOX,
    radiobutton: COMPONENT_TYPE_RADIOBUTTON,
    button: COMPONENT_TYPE_BUTTON,
  };

  return {
    components: bodyForm.components.map((component: FormComponent) => ({
      ...component,
      type: typeMap[component.type.toLowerCase()] || component.type,
    })),
  };
};

export const useFetchFormByLink = (formLink: string) => {
  const [{ data, fetching, error }, reexecuteQuery] = useQuery<GetFormByLinkIdResponse>({
    query: GET_FORM_BY_LINK_ID_QUERY,
    variables: {
      formLink,
    },
    pause: !formLink,
    requestPolicy: 'network-only',
  });

  const rawFormData = data?.getFormByLinkId?.bodyForm ?? null;
  const formData = useMemo(() => normalizeFormData(rawFormData), [rawFormData]);

  return {
    form: data?.getFormByLinkId,
    formData,
    fetching,
    error,
    refetch: reexecuteQuery,
  };
};
